import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { MagnifyingGlass, Car, User, CalendarBlank, ShieldCheck } from "@phosphor-icons/react";
import { StatusPill } from "./lux";
import { StageBadge } from "../pages/insurance/InsuranceList";

export const GlobalSearchBar = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q || q.trim().length < 2) { setResults(null); setOpen(false); setLoading(false); return; }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: q.trim() } });
        setResults(data);
        setOpen(true);
      } catch { setResults(null); }
      finally { setLoading(false); }
    }, 300);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // ⌘K / Ctrl+K focuses the search field from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); setResults(null); navigate(path); };
  const empty = results && !results.vehicles?.length && !results.customers?.length
    && !results.bookings?.length && !results.insurance?.length;

  return (
    <div ref={boxRef} className="relative w-full max-w-xl" data-testid="global-search">
      <div className="relative">
        <MagnifyingGlass size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (results) setOpen(true); }}
          onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); e.currentTarget.blur(); } }}
          placeholder="Search claims, vehicles, clients, bookings…"
          className="pl-11 pr-16 h-10 bg-white/[0.04] border-border rounded-full text-sm placeholder:text-muted-foreground/70 focus-visible:border-primary/40"
          data-testid="global-search-input"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 hidden lg:flex items-center gap-0.5 px-1.5 py-0.5 rounded-md border border-border bg-white/[0.03] text-[10px] text-muted-foreground pointer-events-none">
          ⌘K
        </span>
      </div>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-2xl shadow-2xl z-50 max-h-[70vh] overflow-y-auto overflow-hidden" data-testid="global-search-results">
          {loading && <div className="p-4 text-sm text-muted-foreground">Searching…</div>}
          {!loading && empty && <div className="p-4 text-sm text-muted-foreground">No matches for “{q}”.</div>}
          {!loading && results?.bookings?.length > 0 && (
            <div className="border-b border-border/50 last:border-b-0">
              <div className="px-4 pt-3 pb-1.5 label-mono">Bookings</div>
              {results.bookings.map((b) => (
                <button key={b.id} onClick={() => go(`/bookings/view/${b.id}`)} data-testid={`search-booking-${b.id}`}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/[0.04] transition-colors">
                  <CalendarBlank size={14} className="text-muted-foreground shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm truncate">{b.customer_name || "Guest"} · {b.vehicle_label || b.booking_type}</span>
                    <span className="block text-xs text-muted-foreground">{b.start_datetime ? new Date(b.start_datetime).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : ""}</span>
                  </span>
                  <StatusPill status={b.status} />
                </button>
              ))}
            </div>
          )}
          {!loading && results?.insurance?.length > 0 && (
            <div className="border-b border-border/50 last:border-b-0">
              <div className="px-4 pt-3 pb-1.5 label-mono">Insurance</div>
              {results.insurance.map((c) => (
                <button key={c.id} onClick={() => go(`/insurance/${c.id}`)} data-testid={`search-insurance-${c.id}`}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/[0.04] transition-colors">
                  <ShieldCheck size={14} className="text-muted-foreground shrink-0" />
                  <span className="flex-1 min-w-0">
                    {/* Claim first, because that is what he searched for — and
                        the case number under it, because 14 rows have no claim
                        number and 2 share one. */}
                    <span className="block text-sm truncate">
                      {c.claim_number ? `Claim ${c.claim_number}` : "No claim #"}
                      {c.client_name ? ` · ${c.client_name}` : ""}
                    </span>
                    <span className="block text-xs text-muted-foreground truncate">
                      {[c.case_number, c.insurance_company].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <StageBadge stage={c.stage} />
                </button>
              ))}
            </div>
          )}
          {!loading && results?.customers?.length > 0 && (
            <div className="border-b border-border/50 last:border-b-0">
              <div className="px-4 pt-3 pb-1.5 label-mono">Clients</div>
              {results.customers.map((c) => (
                <button key={c.id} onClick={() => go(`/customers/${c.id}`)} data-testid={`search-customer-${c.id}`}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/[0.04] transition-colors">
                  <User size={14} className="text-muted-foreground shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm truncate">{c.name || "—"}</span>
                    <span className="block text-xs text-muted-foreground truncate">{c.phone || c.email || ""}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {!loading && results?.vehicles?.length > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1.5 label-mono">Vehicles</div>
              {results.vehicles.map((v) => (
                <button key={v.id} onClick={() => go("/fleet")} data-testid={`search-vehicle-${v.id}`}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/[0.04] transition-colors">
                  <Car size={14} className="text-muted-foreground shrink-0" />
                  <span className="flex-1 text-sm truncate">{v.label || "—"}</span>
                  {v.plate && <span className="text-xs text-muted-foreground font-mono">{v.plate}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearchBar;
